// src/pages/GenomeBrowser/ComparisonGenomeBrowser.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Alert } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components (reuse from ComparisonProfile)
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";
import BreadCrumb from "../../Components/Common/BreadCrumb";

// Page-specific Components (reuse from ComparisonProfile)
import GroupsManager from "../ComparisonProfile/components/GroupsManager";

// API
import { getComparisonGenomeBrowserData } from "../../helpers/eirna_backend_helper";

// Hooks (reuse from ComparisonProfile)
import { useFileSelection } from "../../hooks/useFileSelection";
import { useGroupsManager } from "../ComparisonProfile/hooks/useGroupsManager";
import { GenomeBrowserTranscriptListener } from "../../hooks/useGenomeBrowserTranscriptListener";

const ComparisonGenomeBrowser = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const scriptElementRef = useRef(null);

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [plotScript, setPlotScript] = useState(null);
  const [bokehLoading, setBokehLoading] = useState(false);
  const [bokehElementId, setBokehElementId] = useState(null);

  // API data
  const [apiData, setApiData] = useState({
    json_files: [],
    message: null,
    script: null,
  });

  // Enhanced hooks with reusable components (same as ComparisonProfile)
  const {
    selectedSeqType,
    selectedGroup,
    setSelectedSeqType,
    setSelectedGroup,
  } = useFileSelection("riboseq");

  const {
    groups,
    addGroup,
    removeGroup,
    updateGroupName,
    updateGroupColor,
    addFilesToGroup,
    removeFilesFromGroup,
    clearGroup,
    autoFillGroups,
    autoLabelGroups,
    clearAllGroups,
    getMasterFileDict,
  } = useGroupsManager();

  // ✅ Function to execute script tags (same as SingleGenomeBrowser)
  const executeScripts = (scriptContent) => {
    if (!scriptContent) return;

    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = scriptContent;

    // Find all script tags
    const scripts = tempDiv.querySelectorAll("script");

    scripts.forEach((oldScript) => {
      const newScript = document.createElement("script");

      // Copy attributes
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });

      // Copy content
      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }

      // Append to body to execute
      document.body.appendChild(newScript);

      // Clean up after execution
      setTimeout(() => {
        try {
          document.body.removeChild(newScript);
        } catch (e) {
          // Script may have been removed already
        }
      }, 100);
    });
  };

  // Exposes 'groups' to bokeh which allows the update button js to work
  useEffect(() => {
    window.bokehBridge = window.bokehBridge || {};
    window.bokehBridge.groups = groups;
  }, [groups]);

  // Takes in a base64 string and decodes to JSON
  function decodeBase64JsonParam(encoded) {
    const urlDecoded = decodeURIComponent(encoded.replace(/\+/g, ' '));
    const base64 = urlDecoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded =
      base64 + '==='.slice((base64.length + 3) % 4);
    const jsonString = atob(padded);
    return JSON.parse(jsonString);
  }

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const html_params = new URLSearchParams(location.search);
        const file_dict_b64 = html_params.get("file_dict")
        if (file_dict_b64) {
          const file_dict = decodeBase64JsonParam(file_dict_b64)
          var groupId = 1
          for (var grp_name in file_dict){
            const grp_col = file_dict[grp_name]["group_color"]
            const str_file_list = file_dict[grp_name]["file_list"]
            const file_list = str_file_list.map((id) => parseInt(id, 10));

            // Add files to the corresponding group
            addFilesToGroup(groupId, file_list);
            updateGroupColor(groupId, grp_col);
            updateGroupName(groupId, grp_name);

            groupId += 1;

          }
        }
        const response = await getComparisonGenomeBrowserData(projectId, html_params);
        setApiData(response);

        // Handle message
        if (response.message && typeof response.message === "string") {
          setMessage(response.message.trim());
        }

        // Set script for plot rendering
        if (response.script) {
          setPlotScript(response.script);
        }

        // Set default seq type preference for riboseq
        const fileTypes = [
          ...new Set(response.json_files?.map((file) => file.file_type) || []),
        ];
        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        const uniqueGroups = [
          ...new Set(
            response.json_files?.map((file) => file.subgroup).filter(Boolean) ||
              []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }
      } catch (err) {
        setError("Failed to load comparison genome browser data");
        toast.error("Failed to load comparison genome browser data");
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchData();
    }
  }, [projectId, setSelectedSeqType, setSelectedGroup]);


  // Cleanup function
  const cleanup = () => {
    // Clean up script element
    if (
      scriptElementRef.current &&
      document.head.contains(scriptElementRef.current)
    ) {
      try {
        document.head.removeChild(scriptElementRef.current);
        scriptElementRef.current = null;
      } catch (e) {
        console.warn("Script element cleanup failed:", e);
      }
    }
  };

  // Execute Bokeh script
  useEffect(() => {
    if (plotScript && window.Bokeh && containerRef.current) {
      setBokehLoading(true);

      // Cleanup previous script
      cleanup();

      try {
        // Extract script ID from the original script tag
        const scriptIdMatch = plotScript.match(/id="([^"]+)"/);
        const scriptId = scriptIdMatch
          ? scriptIdMatch[1]
          : `bokeh-${Date.now()}`;

        console.log("Creating Bokeh element with ID:", scriptId);
        setBokehElementId(scriptId);

        // Clear container and create the target element FIRST
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
          const targetDiv = document.createElement("div");
          targetDiv.id = scriptId;
          targetDiv.style.width = "100%";
          targetDiv.style.minHeight = "600px";
          containerRef.current.appendChild(targetDiv);

          // Now extract and execute the script content
          const scriptContent = plotScript.replace(
            /<script[^>]*>|<\/script>/g,
            ""
          );

          // Execute the script in a way that preserves the global context
          const scriptElement = document.createElement("script");
          scriptElement.textContent = scriptContent;
          scriptElementRef.current = scriptElement;

          // Add the script to head to execute it
          document.head.appendChild(scriptElement);

          // Check if plot loaded after a delay
          setTimeout(() => {
            setBokehLoading(false);
            const element = document.getElementById(scriptId);
            if (element && element.children.length > 0) {
              console.log("Bokeh plot loaded successfully");
            } else {
              console.warn("Bokeh plot may not have loaded properly");
            }
          }, 3000);
        }
      } catch (err) {
        console.error("Error executing Bokeh script:", err);
        setBokehLoading(false);
        setError("Failed to render genome browser plot");
      }
    }

    // Cleanup function for useEffect
    return cleanup;
  }, [plotScript]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, []);

  // Filter files based on selected seq type and group (same as ComparisonProfile)
  const filteredFiles = useMemo(() => {
    let files = apiData.json_files || [];

    // ✅ Include files already assigned to groups (important fix from earlier discussion)
    const assignedFileIds = new Set();
    groups.forEach((group) => {
      group.files.forEach((fileId) => {
        assignedFileIds.add(fileId);
      });
    });

    return files.filter((file) => {
      const matchesSeqType =
        selectedSeqType === "all" || file.file_type === selectedSeqType;
      const matchesGroup =
        selectedGroup === "all" || file.subgroup === selectedGroup;
      const isAssigned = assignedFileIds.has(file.file_id);

      // Show file if it matches the filter OR is already assigned to a group
      return (matchesSeqType && matchesGroup) || isAssigned;
    });
  }, [apiData.json_files, selectedSeqType, selectedGroup, groups]);

  // Get unique seq types and groups (same as ComparisonProfile)
  const seqTypes = useMemo(() => {
    const types = [
      ...new Set(apiData.json_files?.map((file) => file.file_type) || []),
    ];
    return types.map((type) => ({
      value: type,
      label: type,
      count:
        apiData.json_files?.filter((file) => file.file_type === type).length ||
        0,
    }));
  }, [apiData.json_files]);

  const availableGroups = useMemo(() => {
    const groupSet = new Set(
      apiData.json_files?.map((file) => file.subgroup) || []
    );
    return Array.from(groupSet)
      .filter((group) => group)
      .map((group) => ({
        value: group,
        label: group,
        count:
          apiData.json_files?.filter((file) => file.subgroup === group)
            .length || 0,
      }));
  }, [apiData.json_files]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#1A6D52" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "Comparison Genome Browser | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Comparison Genome Browser" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <h4
                style={{
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#1f2937",
                  marginBottom: "0",
                }}
              >
                Comparison Genome Browser
              </h4>
            </Col>
          </Row>

          {/* Message Alert */}
          {message && (
            <Row className="mb-4">
              <Col lg={12}>
                <Alert color="info">
                  <i className="ri-information-line me-2"></i>
                  {message}
                </Alert>
              </Col>
            </Row>
          )}

          {/* Error Alert */}
          {error && (
            <Row className="mb-4">
              <Col lg={12}>
                <Alert color="danger">
                  <i className="ri-error-warning-line me-2"></i>
                  {error}
                </Alert>
              </Col>
            </Row>
          )}

          {/* Top Row - Seq Types/Groups */}
          <Row className="mb-4">
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={availableGroups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                showSelectAllCheckboxes={false}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          {/* Groups Manager - Same as ComparisonProfile */}
          <Row className="mb-4">
            <Col lg={12}>
              <GroupsManager
                groups={groups}
                allFiles={apiData.json_files} // Pass all files for reference
                filteredFiles={filteredFiles} // Pass filtered files for selection
                onAddGroup={addGroup}
                onRemoveGroup={removeGroup}
                onUpdateGroupName={updateGroupName}
                onUpdateGroupColor={updateGroupColor}
                onAddFilesToGroup={addFilesToGroup}
                onRemoveFilesFromGroup={removeFilesFromGroup}
                onClearGroup={clearGroup}
                onAutoFill={() => autoFillGroups(filteredFiles)}
                onAutoLabel={() => autoLabelGroups(apiData.json_files)}
                onClearAll={clearAllGroups}
              />
            </Col>
          </Row>

          {/* ✅ Genome Browser Plot Container */}
          <Row>
            <Col lg={12}>

              <GenomeBrowserTranscriptListener />

              <div
                ref={containerRef}
                className="genome-browser-container"
                style={{
                  minHeight: "600px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "20px",
                  backgroundColor: "#ffffff",
                }}
              >
                {!plotScript && (
                  <div className="text-center py-5">
                    <div className="text-muted">
                      <i
                        className="ri-bar-chart-group-line"
                        style={{ fontSize: "3rem", opacity: 0.5 }}
                      />
                      <p className="mt-3 mb-0">
                        Comparison genome browser will load here
                      </p>
                      <small>
                        Organize files into groups and the browser will display
                        automatically
                      </small>
                    </div>
                  </div>
                )}

                {/* ✅ DOM elements that the Update Tracks button will look for */}
                <div id="genome-browser-groups" style={{ display: "none" }}>
                  {groups.map((group, index) => (
                    <div key={group.id}>
                      {/* Group file list - the Update Tracks button looks for this */}
                      <ul id={`group_${index + 1}_file_list`}>
                        {group.files.map((fileId) => (
                          <li key={fileId} id={fileId.toString()}>
                            {
                              apiData.json_files?.find(
                                (f) => f.file_id === fileId
                              )?.file_name
                            }
                          </li>
                        ))}
                      </ul>

                      {/* Group color - the Update Tracks button looks for this */}
                      <input
                        type="color"
                        id={`group${index + 1}_color`}
                        value={group.color}
                        readOnly
                      />

                      {/* Group label - the Update Tracks button looks for this */}
                      <input
                        type="text"
                        name={`group_${index + 1}_label`}
                        value={group.name}
                        readOnly
                      />
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          </Row>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ComparisonGenomeBrowser;
